<template>
  <div class="space-y-6">
    <div class="flex justify-between items-center">
      <h1 class="text-2xl font-bold text-gray-900">Azure Storage Management</h1>
      <button @click="refreshAll" class="btn btn-primary" :disabled="loading">
        {{ loading ? 'Refreshing...' : 'Refresh All' }}
      </button>
    </div>

    <!-- Error Display -->
    <div v-if="error" class="bg-red-50 border border-red-200 rounded-md p-4">
      <div class="flex">
        <div class="ml-3">
          <h3 class="text-sm font-medium text-red-800">Error</h3>
          <div class="mt-2 text-sm text-red-700">{{ error }}</div>
        </div>
      </div>
    </div>

    <!-- Storage Statistics -->
    <div v-if="stats" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <div class="card">
        <h3 class="text-sm font-medium text-gray-500 uppercase tracking-wide">Storage Connection</h3>
        <div class="mt-2 text-3xl font-bold" :class="stats.storageConnected ? 'status-active' : 'status-revoked'">
          {{ stats.storageConnected ? 'Connected' : 'Disconnected' }}
        </div>
      </div>

      <div class="card">
        <h3 class="text-sm font-medium text-gray-500 uppercase tracking-wide">API Keys in Storage</h3>
        <div class="mt-2 text-3xl font-bold text-gray-900">{{ stats.totalApiKeysInStorage }}</div>
        <div class="mt-1 text-sm text-gray-600">
          Active: {{ stats.activeApiKeysInStorage }} | Expired: {{ stats.expiredApiKeysInStorage }}
        </div>
      </div>

      <div class="card">
        <h3 class="text-sm font-medium text-gray-500 uppercase tracking-wide">Plans in Storage</h3>
        <div class="mt-2 text-3xl font-bold text-gray-900">{{ stats.totalPlansInStorage }}</div>
      </div>

      <div class="card">
        <h3 class="text-sm font-medium text-gray-500 uppercase tracking-wide">Today's Snapshots</h3>
        <div class="mt-2 text-3xl font-bold text-gray-900">{{ stats.totalSnapshotsToday }}</div>
        <div class="mt-1 text-sm text-gray-600">
          Valid: {{ stats.validSnapshotsToday }} | Expired: {{ stats.expiredSnapshotsToday }}
        </div>
      </div>
    </div>

    <!-- Tabs -->
    <div class="border-b border-gray-200">
      <nav class="-mb-px flex space-x-8">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          @click="activeTab = tab.id"
          :class="[
            'py-2 px-1 border-b-2 font-medium text-sm',
            activeTab === tab.id
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          ]"
        >
          {{ tab.name }}
        </button>
      </nav>
    </div>

    <!-- Tab Content -->
    <div class="mt-6">
      <!-- API Keys Tab -->
      <div v-if="activeTab === 'apikeys'" class="space-y-4">
        <div class="flex justify-between items-center">
          <h2 class="text-lg font-medium text-gray-900">API Keys in Table Storage</h2>
          <button @click="showCreateApiKeyModal = true" class="btn btn-primary">
            Add API Key
          </button>
        </div>

        <!-- Search Bar -->
        <div class="bg-gray-50 p-4 rounded-lg border">
          <div class="flex items-center space-x-4">
            <div class="flex-1">
              <label class="block text-sm font-medium text-gray-700 mb-2">🔍 Search API Keys</label>
              <input
                v-model="searchQuery"
                type="text"
                placeholder="Search by API Key ID, Subscription ID, Plan, Status, or any visible information..."
                class="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div class="flex items-end">
              <button
                @click="clearSearch"
                v-if="searchQuery"
                class="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-100"
              >
                Clear
              </button>
            </div>
          </div>
          <div class="mt-2 text-xs text-gray-500">
            Showing {{ filteredApiKeys.length }} of {{ storageApiKeys.length }} API keys
          </div>
        </div>

        <div v-if="loadingApiKeys" class="text-center py-4">Loading API keys...</div>
        <div v-else-if="storageApiKeys.length === 0" class="text-center py-8 text-gray-500">
          No API keys found in storage
        </div>
        <div v-else-if="filteredApiKeys.length === 0" class="text-center py-8 text-gray-500">
          No API keys match the current filters
        </div>
        <div v-else class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subscription ID</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expires At</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="apiKey in filteredApiKeys" :key="apiKey.id">
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{{ apiKey.id }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <span v-if="apiKey.subscriptionId" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {{ apiKey.subscriptionId }}
                  </span>
                  <span v-else class="text-gray-400 italic">No Subscription ID</span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ apiKey.plan }}</td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span :class="getStatusClass(apiKey.status)" class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full">
                    {{ getStatusText(apiKey.status) }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {{ apiKey.expiresAt ? formatDate(apiKey.expiresAt) : 'Never' }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button @click="editStorageApiKey(apiKey)" class="text-blue-600 hover:text-blue-900 mr-3">Edit</button>
                  <button @click="deleteStorageApiKey(apiKey.id)" class="text-red-600 hover:text-red-900">Delete</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Plans Tab -->
      <div v-if="activeTab === 'plans'" class="space-y-4">
        <div class="flex justify-between items-center">
          <h2 class="text-lg font-medium text-gray-900">Plans in Table Storage</h2>
          <button @click="showCreatePlanModal = true" class="btn btn-primary">
            Add Plan
          </button>
        </div>

        <div v-if="loadingPlans" class="text-center py-4">Loading plans...</div>
        <div v-else-if="storagePlans.length === 0" class="text-center py-8 text-gray-500">
          No plans found in storage
        </div>
        <div v-else class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Minute Limit</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Day Limit</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Month Limit</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="plan in storagePlans" :key="plan.name">
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{{ plan.name }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ plan.minuteLimit }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ plan.dayLimit }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ plan.monthLimit }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button @click="editStoragePlan(plan)" class="text-blue-600 hover:text-blue-900 mr-3">Edit</button>
                  <button @click="deleteStoragePlan(plan.name)" class="text-red-600 hover:text-red-900">Delete</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Snapshots Tab -->
      <div v-if="activeTab === 'snapshots'" class="space-y-4">
        <div class="flex justify-between items-center">
          <h2 class="text-lg font-medium text-gray-900">Rate Limit Snapshots</h2>
          <div class="flex space-x-2">
            <select v-model="snapshotFilter.window" @change="loadSnapshots" class="form-select">
              <option value="">All Windows</option>
              <option value="minute">Minute</option>
              <option value="day">Day</option>
              <option value="month">Month</option>
            </select>
            <label class="flex items-center">
              <input type="checkbox" v-model="snapshotFilter.onlyValid" @change="loadSnapshots" class="mr-2">
              Only Valid
            </label>
            <button
              @click="clearAllSnapshots"
              :disabled="clearingSnapshots"
              class="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
            >
              {{ clearingSnapshots ? 'Clearing...' : 'Clear All Snapshots' }}
            </button>
          </div>
        </div>

        <div v-if="loadingSnapshots" class="text-center py-4">Loading snapshots...</div>
        <div v-else-if="snapshots.length === 0" class="text-center py-8 text-gray-500">
          No snapshots found
        </div>
        <div v-else class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">API Key</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Window</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Snapshot Time</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expires At</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="snapshot in snapshots" :key="snapshot.rowKey">
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{{ snapshot.apiKeyId }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ snapshot.window }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ snapshot.value }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ formatDate(snapshot.snapshotUtc) }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ formatDate(snapshot.expiresAt) }}</td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span :class="snapshot.isValid ? 'status-active' : 'status-revoked'" class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full">
                    {{ snapshot.isValid ? 'Valid' : 'Expired' }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Create API Key Modal -->
    <div v-if="showCreateApiKeyModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" @click="closeApiKeyModal">
      <div class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white" @click.stop>
        <h3 class="text-lg font-bold text-gray-900 mb-4">Add API Key to Azure Storage</h3>

        <form @submit.prevent="submitApiKeyForm" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">API Key ID</label>
            <input
              v-model="apiKeyFormData.id"
              type="text"
              required
              placeholder="Enter unique API key ID"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Subscription ID</label>
            <input
              v-model="apiKeyFormData.subscriptionId"
              type="text"
              placeholder="Enter customer subscription ID (optional)"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p class="text-xs text-gray-500 mt-1">Optional: Customer subscription identifier for shop integration</p>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Plan</label>
            <input
              v-model="apiKeyFormData.plan"
              type="text"
              required
              placeholder="Enter plan name"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label class="flex items-center">
              <input
                type="checkbox"
                v-model="apiKeyFormData.isRevoked"
                class="mr-2 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
              />
              <span class="text-sm font-medium text-gray-700">Revoked</span>
            </label>
            <p class="text-xs text-gray-500 mt-1">Check to revoke this API key (prevents loading into Redis)</p>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Expires At *</label>
            <input
              v-model="apiKeyFormData.expiresAt"
              type="datetime-local"
              required
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p class="text-xs text-gray-500 mt-1">Required: Set when this API key should expire</p>
          </div>

          <div class="flex justify-end space-x-3 pt-4">
            <button type="button" @click="closeApiKeyModal" class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300">
              Cancel
            </button>
            <button type="submit" class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700" :disabled="submittingApiKey">
              {{ submittingApiKey ? 'Adding...' : 'Add API Key' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Create Plan Modal -->
    <div v-if="showCreatePlanModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" @click="closePlanModal">
      <div class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white" @click.stop>
        <h3 class="text-lg font-bold text-gray-900 mb-4">Add Plan to Azure Storage</h3>

        <form @submit.prevent="submitPlanForm" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Plan Name</label>
            <input
              v-model="planFormData.name"
              type="text"
              required
              placeholder="Enter plan name"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Minute Limit</label>
            <input
              v-model.number="planFormData.minuteLimit"
              type="number"
              required
              min="1"
              placeholder="Requests per minute"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Day Limit</label>
            <input
              v-model.number="planFormData.dayLimit"
              type="number"
              required
              min="1"
              placeholder="Requests per day"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Month Limit</label>
            <input
              v-model.number="planFormData.monthLimit"
              type="number"
              required
              min="1"
              placeholder="Requests per month"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div class="flex justify-end space-x-3 pt-4">
            <button type="button" @click="closePlanModal" class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300">
              Cancel
            </button>
            <button type="submit" class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700" :disabled="submittingPlan">
              {{ submittingPlan ? 'Adding...' : 'Add Plan' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Edit API Key Modal -->
    <div v-if="showEditApiKeyModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" @click="closeApiKeyModal">
      <div class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white" @click.stop>
        <h3 class="text-lg font-bold text-gray-900 mb-4">Edit API Key</h3>

        <form @submit.prevent="submitApiKeyForm" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">API Key ID</label>
            <input
              v-model="apiKeyFormData.id"
              type="text"
              required
              readonly
              class="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 focus:outline-none"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Subscription ID</label>
            <input
              v-model="apiKeyFormData.subscriptionId"
              type="text"
              placeholder="Enter customer subscription ID (optional)"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p class="text-xs text-gray-500 mt-1">Optional: Customer subscription identifier for shop integration</p>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Plan</label>
            <input
              v-model="apiKeyFormData.plan"
              type="text"
              required
              placeholder="Enter plan name"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label class="flex items-center">
              <input
                type="checkbox"
                v-model="apiKeyFormData.isRevoked"
                class="mr-2 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
              />
              <span class="text-sm font-medium text-gray-700">Revoked</span>
            </label>
            <p class="text-xs text-gray-500 mt-1">Check to revoke this API key (prevents loading into Redis)</p>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Expires At *</label>
            <input
              v-model="apiKeyFormData.expiresAt"
              type="datetime-local"
              required
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p class="text-xs text-gray-500 mt-1">Required: Set when this API key should expire</p>
          </div>

          <div class="flex justify-end space-x-3 pt-4">
            <button type="button" @click="closeApiKeyModal" class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300">
              Cancel
            </button>
            <button type="submit" class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700" :disabled="submittingApiKey">
              {{ submittingApiKey ? 'Updating...' : 'Update API Key' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Edit Plan Modal -->
    <div v-if="showEditPlanModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" @click="closePlanModal">
      <div class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white" @click.stop>
        <h3 class="text-lg font-bold text-gray-900 mb-4">Edit Plan</h3>

        <form @submit.prevent="submitPlanForm" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Plan Name</label>
            <input
              v-model="planFormData.name"
              type="text"
              required
              readonly
              class="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 focus:outline-none"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Minute Limit</label>
            <input
              v-model.number="planFormData.minuteLimit"
              type="number"
              required
              min="1"
              placeholder="Requests per minute"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Day Limit</label>
            <input
              v-model.number="planFormData.dayLimit"
              type="number"
              required
              min="1"
              placeholder="Requests per day"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Month Limit</label>
            <input
              v-model.number="planFormData.monthLimit"
              type="number"
              required
              min="1"
              placeholder="Requests per month"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div class="flex justify-end space-x-3 pt-4">
            <button type="button" @click="closePlanModal" class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300">
              Cancel
            </button>
            <button type="submit" class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700" :disabled="submittingPlan">
              {{ submittingPlan ? 'Updating...' : 'Update Plan' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { adminApi } from '@/services/api'
import type {
  AzureStorageStats,
  TableStorageApiKey,
  TableStoragePlan,
  RatelimitSnapshot,
  ApiKeyStatus,
  CreateTableStorageApiKey,
  CreateTableStoragePlan
} from '@/types'

const loading = ref(false)
const error = ref<string | null>(null)
const stats = ref<AzureStorageStats | null>(null)
const activeTab = ref('apikeys')

// API Keys
const loadingApiKeys = ref(false)
const storageApiKeys = ref<TableStorageApiKey[]>([])
const showCreateApiKeyModal = ref(false)
const showEditApiKeyModal = ref(false)
const submittingApiKey = ref(false)
const editingApiKey = ref<TableStorageApiKey | null>(null)
const apiKeyFormData = ref({
  id: '',
  plan: '',
  isRevoked: false,
  expiresAt: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16), // 1 year from now
  subscriptionId: ''
})

// Search functionality
const searchQuery = ref('')

const filteredApiKeys = computed(() => {
  if (!searchQuery.value.trim()) {
    return storageApiKeys.value
  }

  const query = searchQuery.value.toLowerCase().trim()

  return storageApiKeys.value.filter(apiKey => {
    // Search across all visible fields
    const searchableText = [
      apiKey.id,
      apiKey.plan,
      apiKey.subscriptionId || '',
      apiKey.status.toString(),
      getStatusText(apiKey.status),
      apiKey.expiresAt || '',
      formatDate(apiKey.expiresAt || '')
    ].join(' ').toLowerCase()

    return searchableText.includes(query)
  })
})

// Plans
const loadingPlans = ref(false)
const storagePlans = ref<TableStoragePlan[]>([])
const showCreatePlanModal = ref(false)
const showEditPlanModal = ref(false)
const submittingPlan = ref(false)
const editingPlan = ref<TableStoragePlan | null>(null)
const planFormData = ref<CreateTableStoragePlan>({
  name: '',
  minuteLimit: 10,
  dayLimit: 1000,
  monthLimit: 30000
})

// Snapshots
const loadingSnapshots = ref(false)
const clearingSnapshots = ref(false)
const snapshots = ref<RatelimitSnapshot[]>([])
const snapshotFilter = ref({
  window: '',
  onlyValid: false
})

const tabs = [
  { id: 'apikeys', name: 'API Keys' },
  { id: 'plans', name: 'Plans' },
  { id: 'snapshots', name: 'Snapshots' }
]

const loadStats = async () => {
  try {
    stats.value = await adminApi.getStorageStats()
  } catch (err: any) {
    console.error('Failed to load storage stats:', err)
  }
}

const loadApiKeys = async () => {
  loadingApiKeys.value = true
  try {
    storageApiKeys.value = await adminApi.getStorageApiKeys()
  } catch (err: any) {
    console.error('Failed to load storage API keys:', err)
  } finally {
    loadingApiKeys.value = false
  }
}

const loadPlans = async () => {
  loadingPlans.value = true
  try {
    storagePlans.value = await adminApi.getStoragePlans()
  } catch (err: any) {
    console.error('Failed to load storage plans:', err)
  } finally {
    loadingPlans.value = false
  }
}

const loadSnapshots = async () => {
  loadingSnapshots.value = true
  try {
    const query = {
      window: snapshotFilter.value.window || undefined,
      onlyValid: snapshotFilter.value.onlyValid || undefined,
      take: 100
    }
    snapshots.value = await adminApi.getSnapshots(query)
  } catch (err: any) {
    console.error('Failed to load snapshots:', err)
  } finally {
    loadingSnapshots.value = false
  }
}

const clearAllSnapshots = async () => {
  if (!confirm('Are you sure you want to delete ALL snapshots? This action cannot be undone.')) {
    return
  }

  clearingSnapshots.value = true
  try {
    const result = await adminApi.deleteAllSnapshots()
    alert(`Successfully deleted ${result.deletedCount} snapshots`)
    await loadSnapshots() // Reload to show empty list
    await loadStats() // Refresh stats
  } catch (err: any) {
    console.error('Failed to clear snapshots:', err)
    alert('Failed to clear snapshots: ' + (err.response?.data?.error || err.message))
  } finally {
    clearingSnapshots.value = false
  }
}

const refreshAll = async () => {
  loading.value = true
  error.value = null
  
  try {
    await Promise.all([
      loadStats(),
      loadApiKeys(),
      loadPlans(),
      loadSnapshots()
    ])
  } catch (err: any) {
    error.value = err.response?.data?.error || err.message || 'Failed to refresh data'
  } finally {
    loading.value = false
  }
}

const getStatusClass = (status: ApiKeyStatus) => {
  switch (status) {
    case 1: return 'status-active' // Active
    case 2: return 'status-revoked' // Expired
    case 3: return 'bg-red-100 text-red-800' // Revoked
    default: return 'bg-gray-100 text-gray-800' // Unknown status
  }
}

const getStatusText = (status: ApiKeyStatus) => {
  switch (status) {
    case 1: return 'Active'
    case 2: return 'Expired'
    case 3: return 'Revoked'
    default: return 'Unknown'
  }
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleString()
}

const editStorageApiKey = (apiKey: TableStorageApiKey) => {
  editingApiKey.value = apiKey
  apiKeyFormData.value = {
    id: apiKey.id,
    plan: apiKey.plan,
    isRevoked: apiKey.status === 3, // 3 = Revoked
    expiresAt: apiKey.expiresAt || '',
    subscriptionId: apiKey.subscriptionId || ''
  }
  showEditApiKeyModal.value = true
}

const deleteStorageApiKey = async (id: string) => {
  if (confirm(`Are you sure you want to delete API key ${id}?`)) {
    try {
      await adminApi.deleteStorageApiKey(id)
      await loadApiKeys()
    } catch (err: any) {
      error.value = `Failed to delete API key: ${err.message}`
    }
  }
}



const editStoragePlan = (plan: TableStoragePlan) => {
  editingPlan.value = plan
  planFormData.value = {
    name: plan.name,
    minuteLimit: plan.minuteLimit,
    dayLimit: plan.dayLimit,
    monthLimit: plan.monthLimit
  }
  showEditPlanModal.value = true
}

const deleteStoragePlan = async (name: string) => {
  if (confirm(`Are you sure you want to delete plan ${name}?`)) {
    try {
      await adminApi.deleteStoragePlan(name)
      await loadPlans()
    } catch (err: any) {
      error.value = `Failed to delete plan: ${err.message}`
    }
  }
}

// API Key Modal Functions
const closeApiKeyModal = () => {
  showCreateApiKeyModal.value = false
  showEditApiKeyModal.value = false
  editingApiKey.value = null
  apiKeyFormData.value = {
    id: '',
    plan: '',
    isRevoked: false,
    expiresAt: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16), // 1 year from now
    subscriptionId: ''
  }
}

const submitApiKeyForm = async () => {
  submittingApiKey.value = true
  error.value = null

  try {
    const formData = { ...apiKeyFormData.value }

    // Validate that expiresAt is provided (required field)
    if (!formData.expiresAt) {
      error.value = 'Expiration date is required'
      return
    }

    // Convert to ISO string
    formData.expiresAt = new Date(formData.expiresAt).toISOString()

    // Convert isRevoked checkbox to status value
    formData.status = formData.isRevoked ? 3 : 1 // 3 = Revoked, 1 = Active
    delete formData.isRevoked // Remove the checkbox field

    await adminApi.upsertStorageApiKey(formData)
    closeApiKeyModal()
    await loadApiKeys()
  } catch (err: any) {
    error.value = err.response?.data?.error || err.message || `Failed to ${editingApiKey.value ? 'update' : 'add'} API key`
  } finally {
    submittingApiKey.value = false
  }
}

// Plan Modal Functions
const closePlanModal = () => {
  showCreatePlanModal.value = false
  showEditPlanModal.value = false
  editingPlan.value = null
  planFormData.value = {
    name: '',
    minuteLimit: 10,
    dayLimit: 1000,
    monthLimit: 30000
  }
}

const submitPlanForm = async () => {
  submittingPlan.value = true
  error.value = null

  try {
    await adminApi.upsertStoragePlan(planFormData.value)
    closePlanModal()
    await loadPlans()
  } catch (err: any) {
    error.value = err.response?.data?.error || err.message || `Failed to ${editingPlan.value ? 'update' : 'add'} plan`
  } finally {
    submittingPlan.value = false
  }
}

// Search Functions
const clearSearch = () => {
  searchQuery.value = ''
}

onMounted(() => {
  refreshAll()
})
</script>
